import { type CARS_VEHICLES_CATEGORIES, CATEGORIES } from '@kijiji/category'
import { type TFunction } from 'next-i18next'

import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { getFuelTypeTranslation } from '@/features/attributes/utils/getFuelTypeTranslation'
import { getHeavyTruckTransmissionTranslation } from '@/features/attributes/utils/getHeavyTruckTransmissionTranslation'
import { getTransmissionTranslation } from '@/features/attributes/utils/getTransmissionTranslation'

/* We can expand this function to return display attributes from any category on the SRP */
type CarsAndVehiclesCategoryId =
  (typeof CARS_VEHICLES_CATEGORIES)[keyof typeof CARS_VEHICLES_CATEGORIES]

const formatAttribute = (
  value: string | undefined,
  options: { label?: string; prefix?: string; suffix?: string } = {}
): string | undefined => {
  const { label, prefix = '', suffix = '' } = options
  if (value === undefined || value === 'other') return undefined
  if (label !== undefined && label !== '' && label !== 'Other') return label
  return `${prefix && `${prefix} `}${value}${suffix && ` ${suffix}`}`
}

/**
 * Returns a list of display-friendly attribute strings for a given Cars & Vehicles category (can be expanded to all categories).
 *
 * This function selects relevant attributes (like mileage, transmission, fuel type, etc.)
 * based on the `categoryId`, and applies formatting rules (e.g., units, prefixes, translation labels)
 * to return a list of human-readable strings for display on the search result page (SRP).
 *
 * @param categoryId - A category ID within the Cars & Vehicles L1 category (e.g., Cars & Trucks, Motorcycles, etc.).
 * @param attributesDictionary - A dictionary of attribute keys to arrays of string values, representing the listing attributes.
 * @param t - A translation function from `next-i18next` used to localize labels and suffixes.
 * @returns A filtered array of formatted attribute strings for display, excluding "other" or undefined values.
 */
export const getDisplayAttributes = (
  categoryId: CarsAndVehiclesCategoryId,
  attributesDictionary: Record<string, string[]>,
  t: TFunction
) => {
  if (!categoryId || !attributesDictionary) return []

  const motorcycleAttributes = [
    formatAttribute(attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0], {
      suffix: t('listing.attributes.kilometers'),
    }),
    formatAttribute(attributesDictionary[ATTRIBUTES.MOTORCYCLE_TRANSMISSION]?.[0], {
      label: t(
        getTransmissionTranslation(
          parseInt(attributesDictionary[ATTRIBUTES.MOTORCYCLE_TRANSMISSION]?.[0])
        )
      ),
    }),
    formatAttribute(attributesDictionary[ATTRIBUTES.MOTORCYCLE_FUEL_TYPE]?.[0], {
      label: t(getFuelTypeTranslation(attributesDictionary[ATTRIBUTES.MOTORCYCLE_FUEL_TYPE]?.[0])),
    }),
  ]

  const atvsAndSnowmobilesAttributes = [
    formatAttribute(attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0], {
      suffix: t('listing.attributes.kilometers'),
    }),
    formatAttribute(attributesDictionary[ATTRIBUTES.ATV_TRANSMISSION]?.[0], {
      label: t(
        getTransmissionTranslation(parseInt(attributesDictionary[ATTRIBUTES.ATV_TRANSMISSION]?.[0]))
      ),
    }),
    formatAttribute(attributesDictionary[ATTRIBUTES.ATV_FUEL_TYPE]?.[0], {
      label: t(getFuelTypeTranslation(attributesDictionary[ATTRIBUTES.ATV_FUEL_TYPE]?.[0])),
    }),
  ]

  const boatsAndWatercraftAttributes = [
    formatAttribute(attributesDictionary[ATTRIBUTES.HOURS]?.[0], {
      suffix: t('listing.attributes.hours'),
    }),
    formatAttribute(attributesDictionary[ATTRIBUTES.BOATS_LENGTH]?.[0], {
      suffix: t('listing.attributes.feet'),
    }),
    formatAttribute(attributesDictionary[ATTRIBUTES.BOATS_ENGINE_POWER]?.[0], {
      suffix: t('listing.attributes.horsepower'),
    }),
  ]

  const DISPLAY_ATTRIBUTES = {
    // Cars & Trucks
    [CATEGORIES.CARS_AND_TRUCKS_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0], {
        suffix: t('listing.attributes.kilometers'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.CAR_TRANSMISSION]?.[0], {
        label: t(
          getTransmissionTranslation(
            parseInt(attributesDictionary[ATTRIBUTES.CAR_TRANSMISSION]?.[0])
          )
        ),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.CAR_FUEL_TYPE]?.[0], {
        label: t(getFuelTypeTranslation(attributesDictionary[ATTRIBUTES.CAR_FUEL_TYPE]?.[0])),
      }),
    ],

    // Motorcycles
    [CATEGORIES.MOTORCYCLES_CATEGORY_ID]: motorcycleAttributes,
    [CATEGORIES.DIRT_BIKES_MOTOCROSS_CATEGORY_ID]: motorcycleAttributes,
    [CATEGORIES.SCOOTERS_POCKET_BIKES_CATEGORY_ID]: motorcycleAttributes,
    [CATEGORIES.SPORT_BIKES_CATEGORY_ID]: motorcycleAttributes,
    [CATEGORIES.SPORT_TOURING_CATEGORY_ID]: motorcycleAttributes,
    [CATEGORIES.TOURING_CATEGORY_ID]: motorcycleAttributes,

    // ATVs & Snowmobiles
    [CATEGORIES.ATVS_SNOWMOBILES_CATEGORY_ID]: atvsAndSnowmobilesAttributes,
    [CATEGORIES.ATVS_CATEGORY_ID]: atvsAndSnowmobilesAttributes,
    [CATEGORIES.ATVS_SNOWMOBILES_OTHERS_CATEGORY_ID]: atvsAndSnowmobilesAttributes,
    [CATEGORIES.SNOWMOBILES_CATEGORY_ID]: atvsAndSnowmobilesAttributes,

    // Boats & Watercrafts
    [CATEGORIES.BOATS_WATERCRAFT_CATEGORY_ID]: boatsAndWatercraftAttributes,
    [CATEGORIES.CANOES_KAYAKS_PADDLE_CATEGORY_ID]: boatsAndWatercraftAttributes,
    [CATEGORIES.PERSONAL_WATERCRAFT_CATEGORY_ID]: boatsAndWatercraftAttributes,
    [CATEGORIES.POWERBOATS_MOTORBOATS_CATEGORY_ID]: boatsAndWatercraftAttributes,
    [CATEGORIES.SAILBOATS_CATEGORY_ID]: boatsAndWatercraftAttributes,

    // Farming Equipment
    [CATEGORIES.FARMING_EQUIPMENT_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.HOURS]?.[0], {
        suffix: t('listing.attributes.hours'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.HORSEPOWER]?.[0], {
        suffix: t('listing.attributes.horsepower'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.LOAD_CAPACITY]?.[0], {
        prefix: t('listing.attributes.up_to'),
        suffix: t('listing.attributes.pounds'),
      }),
    ],

    // RVs & Motorhomes
    [CATEGORIES.RVS_MOTORHOMES_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0], {
        suffix: t('listing.attributes.kilometers'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.MOTORHOMES_LENGTH]?.[0], {
        suffix: t('listing.attributes.feet'),
      }),
    ],

    // Travel Trailers & Campers
    [CATEGORIES.TRAVEL_TRAILERS_CAMPERS_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.CAMPERS_LENGTH]?.[0], {
        suffix: t('listing.attributes.feet'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.SLEEPING_CAPACITY]?.[0], {
        prefix: t('listing.attributes.sleeps'),
      }),
    ],

    // Park Models
    [CATEGORIES.PARK_MODELS_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.PARK_MODELS_LENGTH]?.[0], {
        suffix: t('listing.attributes.feet'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.PARK_MODELS_AREA]?.[0], {
        suffix: t('listing.attributes.square_feet'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.SLEEPING_CAPACITY]?.[0], {
        prefix: t('listing.attributes.sleeps'),
      }),
    ],

    // Cargo & Utility Trailers
    [CATEGORIES.CARGO_UTILITY_TRAILERS_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.LENGTH]?.[0], {
        suffix: t('listing.attributes.feet'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.WEIGHTS_PAYLOAD_CAPACITY]?.[0], {
        prefix: t('listing.attributes.up_to'),
        suffix: t('listing.attributes.pounds'),
      }),
    ],

    // Heavy Trucks
    [CATEGORIES.HEAVY_TRUCKS_CATEGORY_ID]: [
      formatAttribute(attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0], {
        suffix: t('listing.attributes.kilometers'),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.GEARBOX]?.[0], {
        label: t(
          getHeavyTruckTransmissionTranslation(attributesDictionary[ATTRIBUTES.GEARBOX]?.[0])
        ),
      }),
      formatAttribute(attributesDictionary[ATTRIBUTES.CAR_ENGINE_POWER]?.[0], {
        suffix: t('listing.attributes.horsepower'),
      }),
    ],
  }

  return (DISPLAY_ATTRIBUTES[categoryId] || []).filter((val) => val !== undefined)
}
